import {
  resolveAccountEntryNextView,
  resolveThirdPartyAccountType,
  type ThirdPartyBindIdentity,
  type ThirdPartyRegisterInitialView,
  validateAccountEntry,
  validateEmailValue,
  validatePasswordPair,
  validatePhoneInput,
  validateSinglePassword,
  type AccountFlowView,
} from '../utils/account-auth-flow'
import { buildGeetestSendCodePayload, buildGraphCodeSendCodePayload } from '../utils/login-sms'

const ACCOUNT_LOGIN_VERSION = 124
const ACCOUNT_APPLICATION_TYPE = 7

const createTimerController = () => {
  const countDown = ref(0)
  const isCountdown = ref(false)
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    countDown.value = 0
    isCountdown.value = false
  }

  const start = (seconds = 60) => {
    clear()
    countDown.value = seconds
    isCountdown.value = true

    const tick = () => {
      if (countDown.value <= 1) {
        clear()
        return
      }

      timer = setTimeout(() => {
        countDown.value -= 1
        tick()
      }, 1000)
    }

    tick()
  }

  const stop = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return {
    countDown,
    isCountdown,
    start,
    clear,
    stop,
  }
}

export const useAccountAuthFlow = () => {
  const appStore = useAppStore()
  const route = useRoute()
  const { $toast } = useNuxtApp()
  const matomo = useMatomo()
  const { invokeV3 } = useLoginApi()
  const { accessCookie } = useLogin()
  const { ensureSelectedCountry } = useLoginCountries()
  const { t } = useAppI18n()

  const currentCountry = computed(() => appStore.usergetCountry)
  const view = ref<AccountFlowView>('accountEntry')
  const loading = ref(false)
  const showRegisterGeetest = ref(false)
  const showRegisterGraphical = ref(false)
  const resetPreviousView = ref<AccountFlowView>('accountEntry')
  const registerBindBackView = ref<AccountFlowView>('registerCreatePassword')
  const thirdPartyBindIdentity = ref<ThirdPartyBindIdentity>({})

  const registerTimer = createTimerController()
  const registerEmailTimer = createTimerController()
  const resetTimer = createTimerController()

  const form = reactive({
    account: '',
    accountError: '',
    loginPassword: '',
    loginPasswordError: '',
    registerEmail: '',
    registerEmailError: '',
    registerEmailCode: '',
    registerEmailCodeError: '',
    registerPassword: '',
    registerPasswordConfirm: '',
    registerPasswordError: '',
    registerPhone: '',
    registerPhoneError: '',
    registerSmsCode: '',
    registerSmsError: '',
    registerFiguresmsError: '',
    resetEmail: '',
    resetEmailError: '',
    resetCode: '',
    resetCodeError: '',
    resetPassword: '',
    resetPasswordConfirm: '',
    resetPasswordError: '',
  })

  const requestState = reactive({
    registerEmailRequestId: '',
    registerSmsRequestId: '',
    phoneVerifyRequestId: '',
    resetRequestId: '',
  })

  const isThirdPartyPhoneOnlyRegister = computed(() => route.query.type === 'register' && !form.account.trim())
  const registerSmsBusinessType = computed(() => (isThirdPartyPhoneOnlyRegister.value ? 1 : 8))

  const clearAccountErrors = () => {
    form.accountError = ''
    form.loginPasswordError = ''
  }

  const clearRegisterErrors = () => {
    form.registerEmailError = ''
    form.registerEmailCodeError = ''
    form.registerPasswordError = ''
    form.registerPhoneError = ''
    form.registerSmsError = ''
    form.registerFiguresmsError = ''
  }

  const clearResetErrors = () => {
    form.resetEmailError = ''
    form.resetCodeError = ''
    form.resetPasswordError = ''
  }

  const resetRegisterState = () => {
    form.registerEmail = ''
    form.registerEmailCode = ''
    form.registerPassword = ''
    form.registerPasswordConfirm = ''
    form.registerPhone = ''
    form.registerSmsCode = ''
    requestState.registerEmailRequestId = ''
    requestState.registerSmsRequestId = ''
    requestState.phoneVerifyRequestId = ''
    registerBindBackView.value = 'registerCreatePassword'
    thirdPartyBindIdentity.value = {}
    clearRegisterErrors()
    registerTimer.clear()
    registerEmailTimer.clear()
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
  }

  const resetResetState = () => {
    form.resetEmail = ''
    form.resetCode = ''
    form.resetPassword = ''
    form.resetPasswordConfirm = ''
    requestState.resetRequestId = ''
    resetPreviousView.value = 'accountEntry'
    clearResetErrors()
    resetTimer.clear()
  }

  const reset = () => {
    view.value = 'accountEntry'
    loading.value = false
    form.account = ''
    form.loginPassword = ''
    clearAccountErrors()
    resetRegisterState()
    resetResetState()
  }

  const backToAccountEntry = () => {
    view.value = 'accountEntry'
    form.loginPassword = ''
    form.loginPasswordError = ''
    resetRegisterState()
    resetResetState()
  }

  // 清空已校验账号并回到账号输入态，用于密码态账号行的清除按钮。
  const clearAccountAndBackToEntry = () => {
    backToAccountEntry()
    form.account = ''
    form.accountError = ''
  }

  const backFromRegister = () => {
    if (view.value === 'registerVerifyEmail') {
      backToAccountEntry()
      return
    }

    if (view.value === 'registerBindPhone') {
      if (registerBindBackView.value === 'registerCreatePassword') {
        view.value = 'registerCreatePassword'
      } else {
        backToAccountEntry()
      }
      form.registerPhoneError = ''
      form.registerSmsError = ''
      return
    }

    backToAccountEntry()
  }

  /** 返回手机号登录页，并清理仅属于邮箱绑定步骤的状态。 */
  const backFromPhoneBindEmail = () => {
    view.value = 'accountEntry'
    form.registerEmail = ''
    form.registerEmailCode = ''
    requestState.registerEmailRequestId = ''
    form.registerEmailError = ''
    form.registerEmailCodeError = ''
    registerEmailTimer.clear()
  }

  const backFromReset = () => {
    if (view.value === 'resetNewPassword') {
      view.value = 'resetVerifyEmail'
      form.resetPassword = ''
      form.resetPasswordConfirm = ''
      form.resetPasswordError = ''
      return
    }

    const previousView = resetPreviousView.value
    form.account = form.resetEmail || form.account
    resetResetState()
    view.value = previousView === 'accountPasswordLogin' ? 'accountPasswordLogin' : 'accountEntry'
  }

  const openResetFlow = async () => {
    resetPreviousView.value = view.value === 'accountPasswordLogin' ? 'accountPasswordLogin' : 'accountEntry'
    clearResetErrors()
    resetTimer.clear()
    requestState.resetRequestId = ''
    form.resetCode = ''
    form.resetPassword = ''
    form.resetPasswordConfirm = ''
    form.resetEmail = form.account.includes('@') ? form.account.trim() : ''
    view.value = 'resetVerifyEmail'
    matomo.trackEvent('af_forgot_password')

    if (form.resetEmail) {
      await sendResetCode()
    }
  }

  const startThirdPartyRegister = (
    email = '',
    initialView: ThirdPartyRegisterInitialView = 'registerCreatePassword',
  ) => {
    reset()
    if (!initialView) {
      return false
    }

    const normalizedEmail = email.trim()
    if (!normalizedEmail && initialView !== 'registerBindPhone' && initialView !== 'registerVerifyEmail') {
      return false
    }

    form.account = initialView === 'registerBindPhone' ? '' : normalizedEmail
    if (initialView === 'registerVerifyEmail') {
      form.registerEmail = normalizedEmail
    }
    registerBindBackView.value = initialView === 'registerBindPhone' ? 'accountEntry' : 'registerCreatePassword'
    view.value = initialView
    return true
  }

  /** 以已授权的第三方身份打开当前登录弹窗内的邮箱绑定流程。 */
  const startThirdPartyBindEmail = (email = '', identity: ThirdPartyBindIdentity = {}) => {
    reset()
    form.registerEmail = email.trim()
    thirdPartyBindIdentity.value = identity
    view.value = 'thirdPartyBindEmail'
  }

  /** 复用已验证的手机号、短信验证码和快捷登录验证 ID，进入绑定邮箱流程。 */
  const startPhoneBindEmail = (phone: string, smsCode: string, phoneVerifyRequestId: string) => {
    reset()
    form.registerPhone = phone.trim()
    form.registerSmsCode = smsCode.trim()
    requestState.phoneVerifyRequestId = phoneVerifyRequestId
    view.value = 'phoneBindEmail'
  }

  /** 从登录页的邮箱注册入口进入邮箱验证码验证页。 */
  const startEmailRegistration = () => {
    reset()
    view.value = 'registerVerifyEmail'
  }

  const submitAccountEntry = async () => {
    const validationMessage = validateAccountEntry(form.account, t)
    if (validationMessage) {
      form.accountError = validationMessage
      return false
    }

    loading.value = true
    form.accountError = ''

    const result = await invokeV3('usercenter$v2usercenteraccountisregister', {
      account: form.account.trim(),
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.v2usercenteraccountisregister
    const nextView = resolveAccountEntryNextView({
      account: form.account.trim(),
      isRegistered: Boolean(node?.data?.isRegister),
    })

    if (nextView === 'unregisteredAccount') {
      form.accountError = t('84006')
      return false
    }

    view.value = nextView
    if (nextView === 'accountPasswordLogin') return await submitPasswordLogin()
    return true
  }

  const submitPasswordLogin = async () => {
    const accountValidationMessage = validateAccountEntry(form.account, t)
    if (accountValidationMessage) {
      form.accountError = accountValidationMessage
      return false
    }

    form.accountError = ''
    const validationMessage = validateSinglePassword(form.loginPassword, t)
    if (validationMessage) {
      form.loginPasswordError = validationMessage
      return false
    }

    loading.value = true
    form.loginPasswordError = ''

    const result = await invokeV3('usercenter$v2usercenterlogin', {
      account: form.account.trim(),
      password: form.loginPassword.trim(),
      version: ACCOUNT_LOGIN_VERSION,
      loginType: form.account.includes('@') ? 1 : 0,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      ip: appStore.userIp,
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.v2usercenterlogin
    if (!node?.success) {
      form.loginPasswordError = t('82932')
      return false
    }

    matomo.trackEvent('af_login_success', { af_type: '账号' })
    await accessCookie(node.data)
    return true
  }

  const sendRegisterEmailCode = async () => {
    if (loading.value || registerEmailTimer.isCountdown.value) return

    const validationMessage = validateEmailValue(form.registerEmail, t)
    if (validationMessage) {
      form.registerEmailError = validationMessage
      return
    }

    const accountResult = await invokeV3('usercenter$v2usercenteraccountisregister', {
      account: form.registerEmail.trim(),
    }).catch(() => null)
    const accountNode = accountResult?.usercenter?.v2usercenteraccountisregister

    if (!accountNode?.success || accountNode.data?.isRegister) {
      form.registerEmailError = accountNode?.extra || t('84006')
      return
    }

    const sendResult = await invokeV3('usercenter$v2usercentersendemail', {
      email: form.registerEmail.trim(),
      emailType: 2,
      languageCode: appStore.LanguageCode,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      version: ACCOUNT_LOGIN_VERSION,
    }).catch(() => null)
    const sendNode = sendResult?.usercenter?.v2usercentersendemail
    if (!sendNode?.success) {
      form.registerEmailError = sendNode?.extra || t('84051')
      return
    }

    form.registerEmailError = ''
    form.registerEmailCodeError = ''
    registerEmailTimer.start()
  }

  /** 为手机号注册流程中的待绑定邮箱发送验证码。 */
  const sendPhoneBindEmailCode = async () => {
    if (loading.value || registerEmailTimer.isCountdown.value) return

    const validationMessage = validateEmailValue(form.registerEmail, t)
    if (validationMessage) {
      form.registerEmailError = validationMessage
      return
    }

    const sendResult = await invokeV3('usercenter$v2usercentersendemail', {
      email: form.registerEmail.trim(),
      emailType: 2,
      languageCode: appStore.LanguageCode,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      version: ACCOUNT_LOGIN_VERSION,
    }).catch(() => null)
    const sendNode = sendResult?.usercenter?.v2usercentersendemail
    if (!sendNode?.success) {
      form.registerEmailError = sendNode?.extra || t('84051')
      return
    }

    form.registerEmailError = ''
    form.registerEmailCodeError = ''
    registerEmailTimer.start()
  }

  const submitRegisterVerifyEmail = async () => {
    const validationMessage = validateEmailValue(form.registerEmail, t)
    if (validationMessage) {
      form.registerEmailError = validationMessage
      return false
    }

    if (!form.registerEmailCode.trim()) {
      form.registerEmailCodeError = t('82412')
      return false
    }

    loading.value = true
    form.registerEmailError = ''
    form.registerEmailCodeError = ''

    const result = await invokeV3('usercenter$v2usercentervalidateemailcode', {
      Email: form.registerEmail.trim(),
      LanguageCode: appStore.LanguageCode,
      UserId: '',
      Code: form.registerEmailCode.trim(),
      ApplicationType: ACCOUNT_APPLICATION_TYPE,
    }).catch(() => null)

    loading.value = false
    const node = result?.usercenter?.v2usercentervalidateemailcode
    if (!node?.success) {
      form.registerEmailCodeError = t('84053')
      return false
    }

    requestState.registerEmailRequestId = node.data?.requestId || ''
    form.account = form.registerEmail.trim()
    view.value = 'registerCreatePassword'
    return true
  }

  /** 验证邮箱后绑定第三方身份，并使用返回的 LoginToken 建立本站登录态。 */
  const submitThirdPartyBindEmail = async () => {
    const validationMessage = validateEmailValue(form.registerEmail, t)
    if (validationMessage) {
      form.registerEmailError = validationMessage
      return false
    }

    if (!form.registerEmailCode.trim()) {
      form.registerEmailCodeError = t('82412')
      return false
    }

    loading.value = true
    form.registerEmailError = ''
    form.registerEmailCodeError = ''

    const verifyResult = await invokeV3('usercenter$v2usercentervalidateemailcode', {
      Email: form.registerEmail.trim(),
      LanguageCode: appStore.LanguageCode,
      UserId: '',
      Code: form.registerEmailCode.trim(),
      ApplicationType: ACCOUNT_APPLICATION_TYPE,
    }).catch(() => null)
    const verifyNode = verifyResult?.usercenter?.v2usercentervalidateemailcode
    if (!verifyNode?.success) {
      loading.value = false
      form.registerEmailCodeError = t('84053')
      return false
    }

    const result = await invokeV3('usercenter$v2usercenterthirdpartylogin', {
      id: thirdPartyBindIdentity.value.id || String(route.query.id || ''),
      accountType: resolveThirdPartyAccountType(
        thirdPartyBindIdentity.value.accountType || String(route.query.logintype || ''),
      ),
      equipmentType: 3,
      version: ACCOUNT_LOGIN_VERSION,
      isBind: true,
      email: form.registerEmail.trim(),
      token: thirdPartyBindIdentity.value.token || String(route.query.token || ''),
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      ip: appStore.userIp,
    }).catch(() => null)

    loading.value = false
    const node = result?.usercenter?.v2usercenterthirdpartylogin
    if (!node?.success) {
      form.registerEmailCodeError = node?.extra || t('84053')
      return false
    }

    await accessCookie(node.data)
    return true
  }

  /** 验证绑定邮箱验证码后，直接完成无密码的手机号注册。 */
  const submitPhoneBindEmail = async () => {
    const validationMessage = validateEmailValue(form.registerEmail, t)
    if (validationMessage) {
      form.registerEmailError = validationMessage
      return false
    }

    if (!form.registerEmailCode.trim()) {
      form.registerEmailCodeError = t('82412')
      return false
    }

    loading.value = true
    form.registerEmailError = ''
    form.registerEmailCodeError = ''

    const verifyResult = await invokeV3('usercenter$v2usercentervalidateemailcode', {
      Email: form.registerEmail.trim(),
      LanguageCode: appStore.LanguageCode,
      UserId: '',
      Code: form.registerEmailCode.trim(),
      ApplicationType: ACCOUNT_APPLICATION_TYPE,
    }).catch(() => null)
    const verifyNode = verifyResult?.usercenter?.v2usercentervalidateemailcode
    if (!verifyNode?.success) {
      loading.value = false
      form.registerEmailCodeError = t('84053')
      return false
    }

    requestState.registerEmailRequestId = verifyNode.data?.requestId || ''
    form.account = form.registerEmail.trim()

    const registerResult = await invokeV3('usercenter$v2usercenterphoneregister', {
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerEmailCode.trim(),
      emailCode: form.registerEmailCode.trim(),
      registrationType: 112,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
      emailVerifyRequestId: requestState.registerEmailRequestId,
      phoneVerifyRequestId: requestState.phoneVerifyRequestId,
      email: form.account,
    }).catch(() => null)

    loading.value = false
    const registerNode = registerResult?.usercenter?.v2usercenterphoneregister
    if (!registerNode?.success) {
      form.registerEmailCodeError = registerNode?.extra || t('84053')
      return false
    }

    matomo.trackEvent('af_login_success', { af_type: '手机号' })
    await accessCookie(registerNode.data)
    return true
  }

  const submitRegisterCreatePassword = async () => {
    const validationMessage = validatePasswordPair(form.registerPassword, form.registerPasswordConfirm, t)
    if (validationMessage) {
      form.registerPasswordError = validationMessage
      matomo.trackEvent('af_login_password', {
        af_judge: '否',
        af_type: validationMessage,
      })
      return false
    }

    form.registerPasswordError = ''
    matomo.trackEvent('af_login_password', {
      af_judge: '是',
      af_type: '',
    })

    loading.value = true
    let success = false
    if (route.query.type === 'register') {
      success = await thirdPartyEmailRegister()
    } else if (requestState.phoneVerifyRequestId) {
      success = await phoneEmailRegister()
    } else {
      success = await emailRegister()
    }
    loading.value = false
    return success
  }

  /** 根据当前区号获取短信通道，接口失败时回退到默认通道。 */
  const getRegisterSmsSendType = async () => {
    const result = await invokeV3('usercenter$v2usercentergetsendtype', {
      areaCode: currentCountry.value.code,
    }).catch(() => null)
    const node = result?.usercenter?.v2usercentergetsendtype

    return Number(node?.data?.Code ?? 0)
  }

  const sendRegisterCodeRequest = async (payload: Record<string, any>) => {
    const smsSendType = await getRegisterSmsSendType()
    const result = await invokeV3('usercenter$v2usercentersendcode', {
      ...payload,
      smsSendType,
    }).catch(() => null)

    const node = result?.usercenter?.v2usercentersendcode
    if (!node?.success) {
      const message = node?.extra || 'error'
      if (showRegisterGraphical.value) {
        form.registerFiguresmsError = message
      } else {
        form.registerSmsError = message
      }
      matomo.trackEvent('af_login_bind_vercode', {
        af_judge: '否',
        af_type: message,
      })
      return false
    }

    requestState.registerSmsRequestId = node.data?.requestId || node.data?.requestid || ''
    form.registerSmsError = ''
    form.registerFiguresmsError = ''
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
    matomo.trackEvent('af_login_bind_vercode', {
      af_judge: '是',
      af_type: '',
    })
    registerTimer.start()
    return true
  }

  const sendRegisterCode = async () => {
    if (loading.value || registerTimer.isCountdown.value) return

    const phoneError = validatePhoneInput(form.registerPhone.trim(), currentCountry.value.code, t)
    if (phoneError) {
      form.registerPhoneError = phoneError
      matomo.trackEvent('af_login_bind_vercode', {
        af_judge: '否',
        af_type: phoneError,
      })
      return
    }

    form.registerPhoneError = ''
    form.registerSmsError = ''

    showRegisterGeetest.value = true
  }

  const handleRegisterGeetestSuccess = async (payload: Record<string, any>) => {
    await sendRegisterCodeRequest(
      buildGeetestSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.registerPhone.trim(),
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: registerSmsBusinessType.value,
          applicationType: ACCOUNT_APPLICATION_TYPE,
        },
        payload as any,
      ),
    )
  }

  const handleRegisterGeetestError = () => {
    showRegisterGeetest.value = false
    showRegisterGraphical.value = true
  }

  const handleRegisterGraphicalSubmit = async (graphCode: string) => {
    await sendRegisterCodeRequest(
      buildGraphCodeSendCodePayload(
        {
          areaCode: currentCountry.value.code,
          phone: form.registerPhone.trim(),
          languageCode: appStore.LanguageCode,
          msgType: 0,
          smsBusinessType: registerSmsBusinessType.value,
          applicationType: ACCOUNT_APPLICATION_TYPE,
        },
        graphCode,
      ),
    )
  }

  const closeRegisterCaptchaLayers = () => {
    showRegisterGeetest.value = false
    showRegisterGraphical.value = false
  }

  const thirdPartyEmailRegister = async () => {
    const result = await invokeV3('usercenter$v2usercenterthirdemailregister', {
      openId: String(route.query.id || ''),
      accountNick: String(route.query.name || ''),
      accountHead: String(route.query.img || ''),
      accountType: String(route.query.logintype || ''),
      email: form.account.trim(),
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 311,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      emailCode: form.registerEmailCode.trim(),
      token: String(route.query.token || ''),
      registrationip: appStore.userIp,
      password: form.registerPassword,
    }).catch(() => null)

    const node = result?.usercenter?.v2usercenterthirdemailregister
    if (!node?.success) {
      form.registerPasswordError = node?.extra
      return false
    }

    form.registerPasswordError = ''
    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '微信'
    if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'
    else if (loginTypeRaw.includes('facebook')) afType = 'facebook'
    matomo.trackEvent('af_login_success', { af_type: afType })
    await accessCookie(node.data)
    return true
  }

  /** 使用已验证邮箱和已创建密码直接完成普通邮箱注册。 */
  const emailRegister = async () => {
    const result = await invokeV3('usercenter$v2usercenteremailregister', {
      areaCode: currentCountry.value.code,
      password: form.registerPassword,
      passwordRepeat: form.registerPasswordConfirm,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      Email: form.account.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 311,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
      requestId: requestState.registerEmailRequestId,
    }).catch(() => null)

    const node = result?.usercenter?.v2usercenteremailregister
    if (!node?.success) {
      form.registerPasswordError = node?.extra
      return false
    }

    form.registerPasswordError = ''
    matomo.trackEvent('af_login_success', { af_type: '邮箱' })
    await accessCookie(node.data)
    return true
  }

  /** 使用快捷登录返回的验证 ID 完成手机号注册并绑定已验证邮箱。 */
  const phoneEmailRegister = async () => {
    const result = await invokeV3('usercenter$v2usercenterphoneregister', {
      areaCode: currentCountry.value.code,
      password: form.registerPassword,
      passwordRepeat: form.registerPasswordConfirm,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      Email: form.account.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 112,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
      requestId: requestState.registerEmailRequestId,
      phoneVerifyRequestId: requestState.phoneVerifyRequestId,
    }).catch(() => null)

    const node = result?.usercenter?.v2usercenterphoneregister
    if (!node?.success) {
      form.registerPasswordError = node?.extra
      return false
    }

    form.registerPasswordError = ''
    matomo.trackEvent('af_login_success', { af_type: '手机号' })
    await accessCookie(node.data)
    return true
  }

  const thirdPartyPhoneRegister = async () => {
    const result = await invokeV3('usercenter$v2usercenterphoneregister', {
      openId: String(route.query.id || ''),
      accountNick: String(route.query.name || ''),
      accountHead: String(route.query.img || ''),
      accountType: String(route.query.logintype || ''),
      areaCode: currentCountry.value.code,
      areaFlag: currentCountry.value.flag,
      phone: form.registerPhone.trim(),
      deviceInformation: 'web',
      version: ACCOUNT_LOGIN_VERSION,
      registrationPlatform: 0,
      deviceCode: import.meta.client ? localStorage.getItem('wiki-bfp') || '' : '',
      msgCode: form.registerSmsCode.trim(),
      registrationType: 112,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      countryCode: currentCountry.value.countryCode,
      languageCode: appStore.LanguageCode,
      registrationip: appStore.userIp,
    }).catch(() => null)

    const node = result?.usercenter?.v2usercenterphoneregister
    if (!node?.success) {
      form.registerSmsError = node?.extra
      return false
    }

    form.registerSmsError = ''
    const loginTypeRaw = String(route.query.logintype || '').toLowerCase()
    let afType = '微信'
    if (loginTypeRaw.includes('google')) afType = '谷歌'
    else if (loginTypeRaw.includes('apple')) afType = 'apple id'
    else if (loginTypeRaw.includes('linkedin')) afType = '领英'
    else if (loginTypeRaw.includes('facebook')) afType = 'facebook'
    matomo.trackEvent('af_login_success', { af_type: afType })
    await accessCookie(node.data)
    return true
  }

  const submitRegisterBindPhone = async () => {
    const phoneError = validatePhoneInput(form.registerPhone.trim(), currentCountry.value.code, t)
    if (phoneError) {
      form.registerPhoneError = phoneError
      matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: phoneError })
      return false
    }

    if (!form.registerSmsCode.trim()) {
      form.registerSmsError = t('82403')
      matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: t('82403') })
      return false
    }

    loading.value = true
    form.registerPhoneError = ''
    form.registerSmsError = ''

    if (route.query.type === 'register') {
      const success = await thirdPartyPhoneRegister()
      loading.value = false
      if (success) {
        matomo.trackEvent('af_login_bind_click', { af_judge: '是', af_type: '' })
      } else {
        matomo.trackEvent('af_login_bind_click', { af_judge: '否', af_type: form.registerSmsError })
      }
      return success
    }

    loading.value = false
    return false
  }

  const sendResetCode = async () => {
    if (loading.value || resetTimer.isCountdown.value) return
    matomo.trackEvent('af_forgot_vercode')

    if (!form.resetEmail.trim()) {
      form.resetEmailError = t('82411')
      return
    }

    if (validateEmailValue(form.resetEmail, t)) {
      form.resetEmailError = t('82928')
      return
    }

    const existsResponse = await invokeV3('usercenter$v2usercenteremailisexist', {
      email: form.resetEmail.trim(),
    }).catch(() => null)

    const existsNode = existsResponse?.usercenter?.v2usercenteremailisexist
    if (!existsNode?.success || !existsNode?.data) {
      form.resetEmailError = existsNode?.data === false ? t('85163') : existsNode?.extra
      return
    }

    const sendResponse = await invokeV3('usercenter$v2usercentersendemail', {
      email: form.resetEmail.trim(),
      emailType: 3,
      languageCode: appStore.LanguageCode,
      applicationType: ACCOUNT_APPLICATION_TYPE,
      version: ACCOUNT_LOGIN_VERSION,
    }).catch(() => null)

    const sendNode = sendResponse?.usercenter?.v2usercentersendemail
    if (!sendNode?.success) {
      form.resetEmailError = sendNode?.extra
      return
    }

    form.resetEmailError = ''
    form.resetCodeError = ''
    resetTimer.start()
  }

  const submitResetVerifyEmail = async () => {
    matomo.trackEvent('af_forgot_next')

    if (!form.resetEmail.trim()) {
      form.resetEmailError = t('85171')
      return false
    }

    if (validateEmailValue(form.resetEmail, t)) {
      form.resetEmailError = t('82928')
      return false
    }

    if (!form.resetCode.trim()) {
      form.resetCodeError = t('82412')
      return false
    }

    loading.value = true
    clearResetErrors()

    const result = await invokeV3('usercenter$v2usercentervalidateemailcode', {
      Email: form.resetEmail.trim(),
      LanguageCode: appStore.LanguageCode,
      UserId: '',
      Code: form.resetCode.trim(),
      ApplicationType: ACCOUNT_APPLICATION_TYPE,
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.v2usercentervalidateemailcode
    if (!node?.success) {
      form.resetCodeError = t('84053')
      return false
    }

    requestState.resetRequestId = node.data?.requestId || ''
    form.account = form.resetEmail.trim()
    view.value = 'resetNewPassword'
    matomo.trackEvent('af_reset_password')
    return true
  }

  const submitResetNewPassword = async () => {
    matomo.trackEvent('af_reset_click')

    if (form.resetPassword !== form.resetPasswordConfirm) {
      form.resetPasswordError = t('82930')
      return false
    }

    const validationMessage = validatePasswordPair(form.resetPassword, form.resetPasswordConfirm, t)
    if (validationMessage) {
      form.resetPasswordError = validationMessage
      return false
    }

    loading.value = true
    form.resetPasswordError = ''

    const result = await invokeV3('usercenter$v2usercenterforgetuserpasstoemail', {
      password: form.resetPassword,
      requestId: requestState.resetRequestId,
      email: form.resetEmail.trim(),
    }).catch(() => null)

    loading.value = false

    const node = result?.usercenter?.v2usercenterforgetuserpasstoemail
    if (!node?.success) {
      form.resetPasswordError = node?.extra
      return false
    }

    form.account = form.resetEmail.trim()
    $toast(1, t('82929'))
    reset()
    return true
  }

  const submit = async () => {
    if (loading.value) return false

    if (view.value === 'accountEntry') return await submitAccountEntry()
    if (view.value === 'accountPasswordLogin') return await submitPasswordLogin()
    if (view.value === 'phoneBindEmail') return await submitPhoneBindEmail()
    if (view.value === 'registerVerifyEmail') return await submitRegisterVerifyEmail()
    if (view.value === 'thirdPartyBindEmail') return await submitThirdPartyBindEmail()
    if (view.value === 'registerCreatePassword') return await submitRegisterCreatePassword()
    if (view.value === 'registerBindPhone') return await submitRegisterBindPhone()
    if (view.value === 'resetVerifyEmail') return await submitResetVerifyEmail()
    return await submitResetNewPassword()
  }

  const isPrimaryEnabled = computed(() => {
    if (view.value === 'accountEntry') return Boolean(form.account.trim() && form.loginPassword.trim())
    if (view.value === 'accountPasswordLogin') return Boolean(form.loginPassword.trim())
    if (view.value === 'phoneBindEmail') return Boolean(form.registerEmail.trim() && form.registerEmailCode.trim())
    if (view.value === 'registerVerifyEmail') return Boolean(form.registerEmail.trim() && form.registerEmailCode.trim())
    if (view.value === 'thirdPartyBindEmail') return Boolean(form.registerEmail.trim() && form.registerEmailCode.trim())
    if (view.value === 'registerCreatePassword') return Boolean(form.registerPassword && form.registerPasswordConfirm)
    if (view.value === 'registerBindPhone') return Boolean(form.registerPhone.trim() && form.registerSmsCode.trim())
    if (view.value === 'resetVerifyEmail') return Boolean(form.resetEmail.trim() && form.resetCode.trim())
    return form.resetPassword.length >= 6 && form.resetPasswordConfirm.length >= 6
  })

  onMounted(() => {
    ensureSelectedCountry()
  })

  onBeforeUnmount(() => {
    registerTimer.stop()
    registerEmailTimer.stop()
    resetTimer.stop()
  })

  return {
    currentCountry,
    form,
    isPrimaryEnabled,
    loading,
    openResetFlow,
    closeRegisterCaptchaLayers,
    registerCountdown: registerTimer.countDown,
    registerEmailCountdown: registerEmailTimer.countDown,
    registerEmailIsCountdown: registerEmailTimer.isCountdown,
    registerFiguresmsError: computed(() => form.registerFiguresmsError),
    registerGraphicalVisible: showRegisterGraphical,
    registerGeetestVisible: showRegisterGeetest,
    registerIsCountdown: registerTimer.isCountdown,
    reset,
    resetCountdown: resetTimer.countDown,
    resetIsCountdown: resetTimer.isCountdown,
    handleRegisterGeetestError,
    handleRegisterGeetestSuccess,
    handleRegisterGraphicalSubmit,
    sendRegisterCode,
    sendRegisterEmailCode,
    sendPhoneBindEmailCode,
    sendResetCode,
    startEmailRegistration,
    startThirdPartyBindEmail,
    startThirdPartyRegister,
    startPhoneBindEmail,
    submit,
    view,
    backFromRegister,
    backFromPhoneBindEmail,
    backFromReset,
    backToAccountEntry,
    clearAccountAndBackToEntry,
  }
}
